/**
 * Plan-based access control utilities
 */

export const PLANS = {
  STARTER: "starter",
  PROFESSIONAL: "professional",
  ENTERPRISE: "enterprise"
};

/**
 * Plan hierarchy for comparison
 */
const PLAN_HIERARCHY = {
  [PLANS.STARTER]: 1,
  [PLANS.PROFESSIONAL]: 2,
  [PLANS.ENTERPRISE]: 3
};

/**
 * Plan pricing (in MAD)
 */
export const PLAN_PRICES = {
  [PLANS.STARTER]: 299,
  [PLANS.PROFESSIONAL]: 499,
  [PLANS.ENTERPRISE]: 2999
};

/**
 * Plan limits configuration
 */
export const PLAN_LIMITS = {
  [PLANS.STARTER]: {
    rooms: 10,
    users: 3
  },
  [PLANS.PROFESSIONAL]: {
    rooms: 50,
    users: 10
  },
  [PLANS.ENTERPRISE]: {
    rooms: Infinity,
    users: Infinity
  }
};

/**
 * Check if user's plan meets or exceeds required plan
 * @param {string} userPlan - User's current plan
 * @param {string} requiredPlan - Required plan level
 * @returns {boolean}
 */
export const hasRequiredPlan = (userPlan, requiredPlan) => {
  if (!userPlan) return false;
  if (!requiredPlan) return true; // No plan requirement
  
  const userLevel = PLAN_HIERARCHY[userPlan] || 1; // Default to STARTER level
  const requiredLevel = PLAN_HIERARCHY[requiredPlan] || 1;
  
  return userLevel >= requiredLevel;
};

/**
 * Get plan display name
 * @param {string} plan - Plan identifier
 * @returns {string}
 */
export const getPlanDisplayName = (plan) => {
  const names = {
    [PLANS.STARTER]: "Starter",
    [PLANS.PROFESSIONAL]: "Professional",
    [PLANS.ENTERPRISE]: "Enterprise"
  };
  return names[plan] || "Starter";
};

/**
 * Get plan price in MAD
 * @param {string} plan - Plan identifier
 * @returns {number}
 */
export const getPlanPrice = (plan) => {
  return PLAN_PRICES[plan] || PLAN_PRICES[PLANS.STARTER];
};

/**
 * Check if feature is accessible for user's plan
 * @param {string} userPlan - User's current plan
 * @param {string} requiredPlan - Required plan for feature
 * @returns {boolean}
 */
export const canAccessFeature = (userPlan, requiredPlan) => {
  return hasRequiredPlan(userPlan, requiredPlan);
};

/**
 * Get room limit for plan
 * @param {string} plan - Plan identifier
 * @returns {number}
 */
export const getRoomLimit = (plan) => {
  return PLAN_LIMITS[plan]?.rooms || PLAN_LIMITS[PLANS.STARTER].rooms;
};

/**
 * Get user limit for plan
 * @param {string} plan - Plan identifier
 * @returns {number}
 */
export const getUserLimit = (plan) => {
  return PLAN_LIMITS[plan]?.users || PLAN_LIMITS[PLANS.STARTER].users;
};

/**
 * Check if user has reached room limit
 * @param {string} plan - User's plan
 * @param {number} currentCount - Current room count
 * @returns {boolean}
 */
export const hasReachedRoomLimit = (plan, currentCount) => {
  const limit = getRoomLimit(plan);
  if (limit === Infinity) return false;
  return currentCount >= limit;
};

/**
 * Check if user has reached user limit
 * @param {string} plan - User's plan
 * @param {number} currentCount - Current user count
 * @returns {boolean}
 */
export const hasReachedUserLimit = (plan, currentCount) => {
  const limit = getUserLimit(plan);
  if (limit === Infinity) return false;
  return currentCount >= limit;
};

/**
 * Get next upgrade plan
 * @param {string} currentPlan - Current plan
 * @returns {string|null}
 */
export const getNextUpgradePlan = (currentPlan) => {
  const currentLevel = PLAN_HIERARCHY[currentPlan] || 1; // Default to STARTER level
  
  if (currentLevel < PLAN_HIERARCHY[PLANS.PROFESSIONAL]) {
    return PLANS.PROFESSIONAL;
  } else if (currentLevel < PLAN_HIERARCHY[PLANS.ENTERPRISE]) {
    return PLANS.ENTERPRISE;
  }
  
  return null; // Already on highest plan
};

